import { selMessage } from "@selector/shared";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import {
  type AttributeDefaultValueMapEntry,
  type Group,
  type InputEditParentInput,
  type Parent,
  type ParentStatistic,
} from "@/graphql.ts";
import { ENTITY_TYPE } from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import {
  DELETE_PARENTS_FAILURE,
  DELETE_SAVED_ITEM_SUCCESS,
  LOAD_PARENT_ATTRIBUTE_DEFAULTS_FAILURE,
  LOAD_PARENT_ATTRIBUTE_DEFAULTS_REQUEST,
  LOAD_PARENT_ATTRIBUTE_DEFAULTS_SUCCESS,
  LOAD_SAMPLE_PARENT_FAILURE,
  LOAD_SAMPLE_PARENT_REQUEST,
  LOAD_SAMPLE_PARENT_STATISTICS_FAILURE,
  LOAD_SAMPLE_PARENT_STATISTICS_REQUEST,
  LOAD_SAMPLE_PARENT_STATISTICS_SUCCESS,
  LOAD_SAMPLE_PARENT_SUCCESS,
  SAVE_PARENT_FAILURE,
  SAVE_PARENT_REQUEST,
  SAVE_PARENT_SUCCESS,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";

/**
 * State definition
 */
export interface IParentState {
  parent: Parent;
  isLoading: boolean;
  isLoadingParentStatistics: boolean;
  parentStatistics: ParentStatistic[]; // might be multiple parents involved in the selection
  attributeDefaults: AttributeDefaultValueMapEntry[];
}

const initialState: IParentState = {
  isLoading: false,
  parent: null,
  isLoadingParentStatistics: false,
  parentStatistics: [],
  attributeDefaults: [],
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_SAMPLE_PARENT_REQUEST]: (state: IParentState, _payload: Parent): IParentState => {
    return {
      ...state,
      isLoading: true,
    };
  },
  [LOAD_PARENT_ATTRIBUTE_DEFAULTS_SUCCESS]: (
    state: IParentState,
    payload: AttributeDefaultValueMapEntry[],
  ): IParentState => {
    return {
      ...state,
      isLoading: false,
      attributeDefaults: payload,
    };
  },
  [LOAD_SAMPLE_PARENT_SUCCESS]: (state: IParentState, payload: Parent): IParentState => {
    return {
      ...state,
      isLoading: false,
      parent: payload,
    };
  },
  [LOAD_SAMPLE_PARENT_FAILURE]: (state: IParentState): IParentState => {
    return {
      ...state,
      isLoading: false,
      parent: null,
    };
  },
  [LOAD_SAMPLE_PARENT_STATISTICS_REQUEST]: (state: IParentState): IParentState => {
    return {
      ...state,
      isLoadingParentStatistics: true,
      parentStatistics: [],
    };
  },
  [LOAD_SAMPLE_PARENT_STATISTICS_SUCCESS]: (state: IParentState, payload: ParentStatistic[]): IParentState => {
    return {
      ...state,
      isLoadingParentStatistics: false,
      parentStatistics: payload,
    };
  },
  [LOAD_SAMPLE_PARENT_STATISTICS_FAILURE]: (state: IParentState): IParentState => {
    return {
      ...state,
      isLoadingParentStatistics: false,
      parentStatistics: [],
    };
  },
  [SAVE_PARENT_SUCCESS]: (state: IParentState, newParent: Parent): IParentState => {
    return {
      ...state,
      parent: {
        ...state.parent,
        name: newParent.name,
        attributeValues: newParent.attributeValues,
      },
    };
  },
});

/**
 * Action creators
 */
export function fetchAttributeDefaults(sampleTypeId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<AttributeDefaultValueMapEntry[]> => {
    dispatch({ type: LOAD_PARENT_ATTRIBUTE_DEFAULTS_REQUEST });

    return webApiClient
      .fetchSampleAttributeDefaults(sampleTypeId)
      .then((response: AttributeDefaultValueMapEntry[]) => {
        dispatch({
          type: LOAD_PARENT_ATTRIBUTE_DEFAULTS_SUCCESS,
          payload: response,
        });

        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PARENT_ATTRIBUTE_DEFAULTS_FAILURE,
          actionPayload: { id: sampleTypeId },
          alertTitle: `Fetching of Attribute Defaults for Parent Failed`,
        }),
      );
  };
}

export function shouldLoadParent(parentId: number) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<Parent> => {
    const parentState: IParentState = getState().parent;

    // fetch the sample only if no loading is currently in progress and sample is either absent or doesn't match the sample ID
    if (!parentState.isLoading) {
      dispatch({ type: LOAD_SAMPLE_PARENT_REQUEST });

      return webApiClient.fetchSampleParent(parentId).then(
        (parent: Parent) => {
          // Fetch attribute defaults of the parent type only if associated to sample types (could have been detached at some point).
          // Use the first of the sample type since their parent attribute defaults/templates are synchronized anyway.
          if (parent.parentType.sampleTypes?.length > 0) {
            dispatch(fetchAttributeDefaults(parent.parentType.sampleTypes[0].id));
          }
          dispatch({
            type: LOAD_SAMPLE_PARENT_SUCCESS,
            payload: parent,
          });
          return parent;
        },
        createErrorHandler(dispatch, {
          actionType: LOAD_SAMPLE_PARENT_FAILURE,
          alertTitle: "Fetching of Sample Parent Failed",
        }),
      );
    } else {
      return Promise.resolve(parentState.parent);
    }
  };
}

export function reloadExistingParent() {
  return (dispatch, getState: () => IAppState): void => {
    const parentState: IParentState = getState().parent;
    const existingParentId: number = parentState.parent && parentState.parent.id;
    if (existingParentId) {
      dispatch(shouldLoadParent(existingParentId));
    }
  };
}

export function loadParentStatistics(parentIds: number[]) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Parent> => {
    dispatch({ type: LOAD_SAMPLE_PARENT_STATISTICS_REQUEST });

    return webApiClient.fetchSampleParentStatistics(parentIds).then(
      (parentStatistics: ParentStatistic[]) => {
        dispatch({
          type: LOAD_SAMPLE_PARENT_STATISTICS_SUCCESS,
          payload: parentStatistics,
        });
        return parentStatistics;
      },
      createErrorHandler(dispatch, {
        actionType: LOAD_SAMPLE_PARENT_STATISTICS_FAILURE,
        alertTitle: "Fetching of Sample Parent Statistics Failed",
        throwError: true, // will be caught by the modal dialog to close itself
      }),
    );
  };
}

export function saveEditedParent(editParentInput: InputEditParentInput) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Parent> => {
    dispatch({ type: SAVE_PARENT_REQUEST });

    return webApiClient.saveParent(editParentInput).then(
      (parent: Parent) => {
        dispatch({
          type: SAVE_PARENT_SUCCESS,
          payload: parent,
        });

        notify.info({
          title: selMessage({
            id: "sampleParent.edit.successTitle",
            values: { parentTypeName: parent.parentType.name },
          }),
          children: selMessage({
            id: "sampleParent.edit.successMessage",
            values: {
              parentName: parent.name,
            },
          }),
        });

        return parent;
      },
      createErrorHandler(dispatch, {
        actionType: SAVE_PARENT_FAILURE,
        alertTitle: "Saving of Parent Data Failed",
        throwError: true, // will be caught by the modal dialog to close itself
      }),
    );
  };
}

export function deleteParents(parentIds: number[], parentTypeName: string) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Group[]> => {
    return webApiClient
      .deleteParentList(parentIds)
      .then((response: Parent[]) => {
        dispatch({
          type: DELETE_SAVED_ITEM_SUCCESS,
          payload: {
            deletedIds: parentIds,
            type: ENTITY_TYPE.SAMPLE_PARENT,
          },
        });

        notify.info({
          title: selMessage({
            id: "sampleParent.delete.successTitle",
            values: { parentTypeName: pluralizeWord(parentTypeName, parentIds.length) },
          }),
          children: selMessage({
            id: "sampleParent.delete.successMessage",
            values: {
              parentTypesCount: parentIds.length,
              parentTypeName: pluralizeWord(parentTypeName, parentIds.length),
            },
          }),
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_PARENTS_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function checkParentNameUniqueness(name: string, parentId: number) {
  return (_dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.checkParentNameUniqueness(name, parentId);
  };
}
